// Canonical copy: futuredocs/packages/permissions/src/roles.js. futuredocs-web keeps a byte-identical copy
// (`npm run permissions:sync` there); `npm run permissions:check` fails on drift.
/**
 * Role hierarchy & permissions for Future Document (DMS + SCM/ERP).
 *
 * Legacy org roles (kept): full_admin → admin → company → dealer → user → viewer
 * SCM operational roles (added): procurement_*, warehouse_*, production_*, quality_*,
 * logistics_*, export_specialist, sales_manager, finance, management
 * Read-only roles: viewer, auditor (local accountant / external auditor, FR-ACC-46)
 *
 * Permission keys support:
 * - Legacy: files_read, inventory_write, …
 * - v2: domain.resource.action (e.g. product.product.view)
 */

export const FULL_ADMIN_EMAIL = "legastive@gmail.com";

export const ROLES = {
  FULL_ADMIN: "full_admin",
  ADMIN: "admin",
  COMPANY: "company",
  DEALER: "dealer",
  USER: "user",
  VIEWER: "viewer",
  // SCM roles
  PROCUREMENT_MANAGER: "procurement_manager",
  PROCUREMENT_SPECIALIST: "procurement_specialist",
  WAREHOUSE_MANAGER: "warehouse_manager",
  WAREHOUSE_OPERATOR: "warehouse_operator",
  PRODUCTION_MANAGER: "production_manager",
  PRODUCTION_OPERATOR: "production_operator",
  QUALITY_MANAGER: "quality_manager",
  QUALITY_INSPECTOR: "quality_inspector",
  LOGISTICS_MANAGER: "logistics_manager",
  EXPORT_SPECIALIST: "export_specialist",
  SALES_MANAGER: "sales_manager",
  FINANCE: "finance",
  MANAGEMENT: "management",
  AUDITOR: "auditor"
};

/** Display / seed order (highest privilege first among legacy, then SCM) */
export const ROLE_ORDER = [
  ROLES.FULL_ADMIN,
  ROLES.ADMIN,
  ROLES.COMPANY,
  ROLES.DEALER,
  ROLES.MANAGEMENT,
  ROLES.PROCUREMENT_MANAGER,
  ROLES.WAREHOUSE_MANAGER,
  ROLES.PRODUCTION_MANAGER,
  ROLES.QUALITY_MANAGER,
  ROLES.LOGISTICS_MANAGER,
  ROLES.SALES_MANAGER,
  ROLES.FINANCE,
  ROLES.PROCUREMENT_SPECIALIST,
  ROLES.WAREHOUSE_OPERATOR,
  ROLES.PRODUCTION_OPERATOR,
  ROLES.QUALITY_INSPECTOR,
  ROLES.EXPORT_SPECIALIST,
  ROLES.AUDITOR,
  ROLES.USER,
  ROLES.VIEWER
];

/** Roles that can be created from Admin Panel (Full Admin cannot be created this way) */
export const CREATABLE_ROLES = [
  ROLES.ADMIN,
  ROLES.COMPANY,
  ROLES.DEALER,
  ROLES.USER,
  ROLES.VIEWER,
  ROLES.PROCUREMENT_MANAGER,
  ROLES.PROCUREMENT_SPECIALIST,
  ROLES.WAREHOUSE_MANAGER,
  ROLES.WAREHOUSE_OPERATOR,
  ROLES.PRODUCTION_MANAGER,
  ROLES.PRODUCTION_OPERATOR,
  ROLES.QUALITY_MANAGER,
  ROLES.QUALITY_INSPECTOR,
  ROLES.LOGISTICS_MANAGER,
  ROLES.EXPORT_SPECIALIST,
  ROLES.SALES_MANAGER,
  ROLES.FINANCE,
  ROLES.MANAGEMENT,
  ROLES.AUDITOR
];

export const ROLE_LEVEL = {
  [ROLES.FULL_ADMIN]: 100,
  [ROLES.ADMIN]: 80,
  [ROLES.COMPANY]: 60,
  [ROLES.DEALER]: 40,
  [ROLES.MANAGEMENT]: 55,
  [ROLES.PROCUREMENT_MANAGER]: 50,
  [ROLES.WAREHOUSE_MANAGER]: 50,
  [ROLES.PRODUCTION_MANAGER]: 50,
  [ROLES.QUALITY_MANAGER]: 50,
  [ROLES.LOGISTICS_MANAGER]: 50,
  [ROLES.SALES_MANAGER]: 50,
  [ROLES.FINANCE]: 48,
  [ROLES.PROCUREMENT_SPECIALIST]: 35,
  [ROLES.WAREHOUSE_OPERATOR]: 35,
  [ROLES.PRODUCTION_OPERATOR]: 35,
  [ROLES.QUALITY_INSPECTOR]: 35,
  [ROLES.EXPORT_SPECIALIST]: 35,
  [ROLES.AUDITOR]: 30,
  [ROLES.USER]: 20,
  [ROLES.VIEWER]: 10
};

/** Legacy permission keys (kept for existing UI) */
export const PERMISSIONS = {
  MANAGE_ROLES: "manage_roles",
  MANAGE_USERS: "manage_users",
  MANAGE_LICENSES: "manage_licenses",
  MANAGE_ORG: "manage_org",
  ACCESS_ADMIN_PANEL: "access_admin_panel",
  FILES_READ: "files_read",
  FILES_WRITE: "files_write",
  INVENTORY_READ: "inventory_read",
  INVENTORY_WRITE: "inventory_write",
  ACCOUNTING_READ: "accounting_read",
  ACCOUNTING_WRITE: "accounting_write",
  APPROVALS_READ: "approvals_read",
  APPROVALS_WRITE: "approvals_write",
  AI_ACCESS: "ai_access",
  SETTINGS_WRITE: "settings_write",
  SEARCH_ACCESS: "search_access",
  DASHBOARD_ACCESS: "dashboard_access",
  // Foundation / SCM (legacy-style keys used by routes)
  MASTER_DATA_READ: "master_data_read",
  MASTER_DATA_WRITE: "master_data_write",
  AUDIT_READ: "audit_read",
  FOUNDATION_USERS: "foundation_users",
  PROCUREMENT_READ: "procurement_read",
  PROCUREMENT_WRITE: "procurement_write",
  MANUFACTURING_READ: "manufacturing_read",
  MANUFACTURING_WRITE: "manufacturing_write",
  QUALITY_READ: "quality_read",
  QUALITY_WRITE: "quality_write",
  PACKAGING_READ: "packaging_read",
  PACKAGING_WRITE: "packaging_write",
  EXPORT_READ: "export_read",
  EXPORT_WRITE: "export_write",
  LOGISTICS_READ: "logistics_read",
  LOGISTICS_WRITE: "logistics_write",
  SALES_READ: "sales_read",
  SALES_WRITE: "sales_write",
  AFTERSALES_READ: "aftersales_read",
  AFTERSALES_WRITE: "aftersales_write",
  ENTERPRISE_READ: "enterprise_read",
  ENTERPRISE_WRITE: "enterprise_write"
};

/**
 * Permission v2 — domain.resource.action
 * Aliased to legacy keys where applicable.
 */
export const PERMISSIONS_V2 = {
  FOUNDATION_USER_VIEW: "foundation.user.view",
  FOUNDATION_USER_MANAGE: "foundation.user.manage",
  FOUNDATION_ROLE_MANAGE: "foundation.role.manage",
  FOUNDATION_MASTER_VIEW: "foundation.master_data.view",
  FOUNDATION_MASTER_MANAGE: "foundation.master_data.manage",
  FOUNDATION_AUDIT_VIEW: "foundation.audit.view",
  FOUNDATION_DOCUMENT_LINK: "foundation.document.link",
  FOUNDATION_STATUS_TRANSITION: "foundation.status.transition",
  PRODUCT_VIEW: "product.product.view",
  PRODUCT_CREATE: "product.product.create",
  PRODUCT_BOM_MANAGE: "product.bom.manage",
  PROCUREMENT_SUPPLIER_VIEW: "procurement.supplier.view",
  PROCUREMENT_SUPPLIER_MANAGE: "procurement.supplier.manage",
  PROCUREMENT_RFQ_MANAGE: "procurement.rfq.manage",
  PROCUREMENT_PO_CREATE: "procurement.purchase_order.create",
  PROCUREMENT_PO_APPROVE: "procurement.purchase_order.approve",
  WAREHOUSE_INVENTORY_VIEW: "warehouse.inventory.view",
  WAREHOUSE_INVENTORY_ADJUST: "warehouse.inventory.adjust",
  MANUFACTURING_ORDER_VIEW: "manufacturing.order.view",
  MANUFACTURING_ORDER_MANAGE: "manufacturing.order.manage",
  MANUFACTURING_ORDER_RELEASE: "manufacturing.order.release",
  QUALITY_PLAN_MANAGE: "quality.plan.manage",
  QUALITY_INSPECTION_RECORD: "quality.inspection.record",
  QUALITY_INSPECTION_APPROVE: "quality.inspection.approve",
  QUALITY_NCR_MANAGE: "quality.ncr.manage",
  PACKAGING_MANAGE: "packaging.package.manage",
  EXPORT_ORDER_MANAGE: "export.order.manage",
  EXPORT_DOC_MANAGE: "export.document.manage",
  LOGISTICS_SHIPMENT_MANAGE: "logistics.shipment.manage",
  CUSTOMS_DECLARE: "customs.declaration.manage",
  SALES_CUSTOMER_MANAGE: "sales.customer.manage",
  SALES_ORDER_MANAGE: "sales.order.manage",
  SALES_FULFILLMENT: "sales.fulfillment.manage",
  AFTERSALES_RETURN_MANAGE: "aftersales.return.manage",
  AFTERSALES_RMA_MANAGE: "aftersales.rma.manage",
  AFTERSALES_WARRANTY_MANAGE: "aftersales.warranty.manage",
  AFTERSALES_SERVICE_MANAGE: "aftersales.service.manage",
  ENTERPRISE_APPROVAL_MANAGE: "enterprise.approval.manage",
  ENTERPRISE_NOTIFY: "enterprise.notification.send",
  ENTERPRISE_REPORT_VIEW: "enterprise.report.view",
  ENTERPRISE_FINANCE_LINK: "enterprise.finance.link",
  FILES_VIEW: "files.file.view",
  FILES_MANAGE: "files.file.manage",
  ACCOUNTING_VIEW: "accounting.invoice.view",
  ACCOUNTING_MANAGE: "accounting.invoice.manage",
  AI_USE: "ai.assistant.use",
  DASHBOARD_VIEW: "management.dashboard.view",
  SEARCH_USE: "files.search.use",
  SETTINGS_MANAGE: "foundation.settings.manage",
  LICENSE_MANAGE: "foundation.license.manage",
  ORG_MANAGE: "foundation.org.manage",
  ADMIN_PANEL: "foundation.admin_panel.access"
};

/** Map any known permission key → canonical set of keys that grant it */
export const PERMISSION_ALIASES = {
  // legacy ↔ v2
  [PERMISSIONS.MANAGE_ROLES]: [PERMISSIONS.MANAGE_ROLES, PERMISSIONS_V2.FOUNDATION_ROLE_MANAGE],
  [PERMISSIONS.MANAGE_USERS]: [PERMISSIONS.MANAGE_USERS, PERMISSIONS_V2.FOUNDATION_USER_MANAGE, PERMISSIONS.FOUNDATION_USERS],
  [PERMISSIONS.MANAGE_LICENSES]: [PERMISSIONS.MANAGE_LICENSES, PERMISSIONS_V2.LICENSE_MANAGE],
  [PERMISSIONS.MANAGE_ORG]: [PERMISSIONS.MANAGE_ORG, PERMISSIONS_V2.ORG_MANAGE],
  [PERMISSIONS.ACCESS_ADMIN_PANEL]: [PERMISSIONS.ACCESS_ADMIN_PANEL, PERMISSIONS_V2.ADMIN_PANEL],
  [PERMISSIONS.FILES_READ]: [PERMISSIONS.FILES_READ, PERMISSIONS_V2.FILES_VIEW],
  [PERMISSIONS.FILES_WRITE]: [PERMISSIONS.FILES_WRITE, PERMISSIONS_V2.FILES_MANAGE],
  [PERMISSIONS.INVENTORY_READ]: [PERMISSIONS.INVENTORY_READ, PERMISSIONS_V2.WAREHOUSE_INVENTORY_VIEW, PERMISSIONS_V2.PRODUCT_VIEW],
  [PERMISSIONS.INVENTORY_WRITE]: [PERMISSIONS.INVENTORY_WRITE, PERMISSIONS_V2.WAREHOUSE_INVENTORY_ADJUST, PERMISSIONS_V2.PRODUCT_CREATE],
  [PERMISSIONS.ACCOUNTING_READ]: [PERMISSIONS.ACCOUNTING_READ, PERMISSIONS_V2.ACCOUNTING_VIEW],
  [PERMISSIONS.ACCOUNTING_WRITE]: [PERMISSIONS.ACCOUNTING_WRITE, PERMISSIONS_V2.ACCOUNTING_MANAGE],
  [PERMISSIONS.APPROVALS_READ]: [PERMISSIONS.APPROVALS_READ],
  [PERMISSIONS.APPROVALS_WRITE]: [PERMISSIONS.APPROVALS_WRITE],
  [PERMISSIONS.AI_ACCESS]: [PERMISSIONS.AI_ACCESS, PERMISSIONS_V2.AI_USE],
  [PERMISSIONS.SETTINGS_WRITE]: [PERMISSIONS.SETTINGS_WRITE, PERMISSIONS_V2.SETTINGS_MANAGE],
  [PERMISSIONS.SEARCH_ACCESS]: [PERMISSIONS.SEARCH_ACCESS, PERMISSIONS_V2.SEARCH_USE],
  [PERMISSIONS.DASHBOARD_ACCESS]: [PERMISSIONS.DASHBOARD_ACCESS, PERMISSIONS_V2.DASHBOARD_VIEW],
  [PERMISSIONS.MASTER_DATA_READ]: [PERMISSIONS.MASTER_DATA_READ, PERMISSIONS_V2.FOUNDATION_MASTER_VIEW],
  [PERMISSIONS.MASTER_DATA_WRITE]: [PERMISSIONS.MASTER_DATA_WRITE, PERMISSIONS_V2.FOUNDATION_MASTER_MANAGE],
  [PERMISSIONS.AUDIT_READ]: [PERMISSIONS.AUDIT_READ, PERMISSIONS_V2.FOUNDATION_AUDIT_VIEW],
  [PERMISSIONS.FOUNDATION_USERS]: [PERMISSIONS.FOUNDATION_USERS, PERMISSIONS.MANAGE_USERS, PERMISSIONS_V2.FOUNDATION_USER_VIEW],
  [PERMISSIONS.PROCUREMENT_READ]: [
    PERMISSIONS.PROCUREMENT_READ,
    PERMISSIONS_V2.PROCUREMENT_SUPPLIER_VIEW,
    PERMISSIONS_V2.PROCUREMENT_RFQ_MANAGE,
    PERMISSIONS_V2.PROCUREMENT_PO_CREATE
  ],
  [PERMISSIONS.PROCUREMENT_WRITE]: [
    PERMISSIONS.PROCUREMENT_WRITE,
    PERMISSIONS_V2.PROCUREMENT_SUPPLIER_MANAGE,
    PERMISSIONS_V2.PROCUREMENT_RFQ_MANAGE,
    PERMISSIONS_V2.PROCUREMENT_PO_CREATE,
    PERMISSIONS_V2.PROCUREMENT_PO_APPROVE,
    PERMISSIONS_V2.PRODUCT_BOM_MANAGE
  ],
  [PERMISSIONS.MANUFACTURING_READ]: [
    PERMISSIONS.MANUFACTURING_READ,
    PERMISSIONS_V2.MANUFACTURING_ORDER_VIEW,
    PERMISSIONS_V2.MANUFACTURING_ORDER_RELEASE
  ],
  [PERMISSIONS.MANUFACTURING_WRITE]: [
    PERMISSIONS.MANUFACTURING_WRITE,
    PERMISSIONS_V2.MANUFACTURING_ORDER_MANAGE,
    PERMISSIONS_V2.MANUFACTURING_ORDER_RELEASE,
    PERMISSIONS_V2.PRODUCT_BOM_MANAGE
  ],
  [PERMISSIONS.QUALITY_READ]: [
    PERMISSIONS.QUALITY_READ,
    PERMISSIONS_V2.QUALITY_PLAN_MANAGE,
    PERMISSIONS_V2.QUALITY_INSPECTION_RECORD,
    PERMISSIONS_V2.QUALITY_INSPECTION_APPROVE,
    PERMISSIONS_V2.QUALITY_NCR_MANAGE
  ],
  [PERMISSIONS.QUALITY_WRITE]: [
    PERMISSIONS.QUALITY_WRITE,
    PERMISSIONS_V2.QUALITY_PLAN_MANAGE,
    PERMISSIONS_V2.QUALITY_INSPECTION_RECORD,
    PERMISSIONS_V2.QUALITY_INSPECTION_APPROVE,
    PERMISSIONS_V2.QUALITY_NCR_MANAGE
  ],
  [PERMISSIONS.PACKAGING_READ]: [
    PERMISSIONS.PACKAGING_READ,
    PERMISSIONS_V2.PACKAGING_MANAGE
  ],
  [PERMISSIONS.PACKAGING_WRITE]: [
    PERMISSIONS.PACKAGING_WRITE,
    PERMISSIONS_V2.PACKAGING_MANAGE
  ],
  [PERMISSIONS.EXPORT_READ]: [
    PERMISSIONS.EXPORT_READ,
    PERMISSIONS_V2.EXPORT_ORDER_MANAGE,
    PERMISSIONS_V2.EXPORT_DOC_MANAGE,
    PERMISSIONS_V2.CUSTOMS_DECLARE
  ],
  [PERMISSIONS.EXPORT_WRITE]: [
    PERMISSIONS.EXPORT_WRITE,
    PERMISSIONS_V2.EXPORT_ORDER_MANAGE,
    PERMISSIONS_V2.EXPORT_DOC_MANAGE,
    PERMISSIONS_V2.CUSTOMS_DECLARE
  ],
  [PERMISSIONS.LOGISTICS_READ]: [
    PERMISSIONS.LOGISTICS_READ,
    PERMISSIONS_V2.LOGISTICS_SHIPMENT_MANAGE
  ],
  [PERMISSIONS.LOGISTICS_WRITE]: [
    PERMISSIONS.LOGISTICS_WRITE,
    PERMISSIONS_V2.LOGISTICS_SHIPMENT_MANAGE
  ],
  [PERMISSIONS.SALES_READ]: [
    PERMISSIONS.SALES_READ,
    PERMISSIONS_V2.SALES_CUSTOMER_MANAGE,
    PERMISSIONS_V2.SALES_ORDER_MANAGE,
    PERMISSIONS_V2.SALES_FULFILLMENT
  ],
  [PERMISSIONS.SALES_WRITE]: [
    PERMISSIONS.SALES_WRITE,
    PERMISSIONS_V2.SALES_CUSTOMER_MANAGE,
    PERMISSIONS_V2.SALES_ORDER_MANAGE,
    PERMISSIONS_V2.SALES_FULFILLMENT
  ],
  [PERMISSIONS.AFTERSALES_READ]: [
    PERMISSIONS.AFTERSALES_READ,
    PERMISSIONS_V2.AFTERSALES_RETURN_MANAGE,
    PERMISSIONS_V2.AFTERSALES_RMA_MANAGE,
    PERMISSIONS_V2.AFTERSALES_WARRANTY_MANAGE,
    PERMISSIONS_V2.AFTERSALES_SERVICE_MANAGE
  ],
  [PERMISSIONS.AFTERSALES_WRITE]: [
    PERMISSIONS.AFTERSALES_WRITE,
    PERMISSIONS_V2.AFTERSALES_RETURN_MANAGE,
    PERMISSIONS_V2.AFTERSALES_RMA_MANAGE,
    PERMISSIONS_V2.AFTERSALES_WARRANTY_MANAGE,
    PERMISSIONS_V2.AFTERSALES_SERVICE_MANAGE
  ],
  [PERMISSIONS.ENTERPRISE_READ]: [
    PERMISSIONS.ENTERPRISE_READ,
    PERMISSIONS_V2.ENTERPRISE_REPORT_VIEW,
    PERMISSIONS_V2.ENTERPRISE_APPROVAL_MANAGE,
    PERMISSIONS.APPROVALS_READ
  ],
  [PERMISSIONS.ENTERPRISE_WRITE]: [
    PERMISSIONS.ENTERPRISE_WRITE,
    PERMISSIONS_V2.ENTERPRISE_APPROVAL_MANAGE,
    PERMISSIONS_V2.ENTERPRISE_NOTIFY,
    PERMISSIONS_V2.ENTERPRISE_FINANCE_LINK,
    PERMISSIONS.APPROVALS_WRITE
  ]
};

// Mirror v2 keys into alias table
Object.values(PERMISSIONS_V2).forEach((v2) => {
  const legacyEntry = Object.entries(PERMISSION_ALIASES).find(([, list]) => list.includes(v2));
  if (legacyEntry) {
    PERMISSION_ALIASES[v2] = legacyEntry[1];
  } else {
    PERMISSION_ALIASES[v2] = [v2];
  }
});

const ALL_LEGACY = Object.values(PERMISSIONS);
const ALL_V2 = Object.values(PERMISSIONS_V2);
const ALL_PERMS = [...new Set([...ALL_LEGACY, ...ALL_V2])];

const READ_PERMS = [
  PERMISSIONS.FILES_READ,
  PERMISSIONS.INVENTORY_READ,
  PERMISSIONS.ACCOUNTING_READ,
  PERMISSIONS.APPROVALS_READ,
  PERMISSIONS.AI_ACCESS,
  PERMISSIONS.SEARCH_ACCESS,
  PERMISSIONS.DASHBOARD_ACCESS,
  PERMISSIONS.MASTER_DATA_READ,
  PERMISSIONS.AUDIT_READ,
  PERMISSIONS.PROCUREMENT_READ,
  PERMISSIONS.MANUFACTURING_READ,
  PERMISSIONS.QUALITY_READ,
  PERMISSIONS.PACKAGING_READ,
  PERMISSIONS.EXPORT_READ,
  PERMISSIONS.LOGISTICS_READ,
  PERMISSIONS.SALES_READ,
  PERMISSIONS.AFTERSALES_READ,
  PERMISSIONS.ENTERPRISE_READ
];

const WRITE_PERMS = [
  PERMISSIONS.FILES_WRITE,
  PERMISSIONS.INVENTORY_WRITE,
  PERMISSIONS.ACCOUNTING_WRITE,
  PERMISSIONS.APPROVALS_WRITE,
  PERMISSIONS.SETTINGS_WRITE,
  PERMISSIONS.MASTER_DATA_WRITE,
  PERMISSIONS.PROCUREMENT_WRITE,
  PERMISSIONS.MANUFACTURING_WRITE,
  PERMISSIONS.QUALITY_WRITE,
  PERMISSIONS.PACKAGING_WRITE,
  PERMISSIONS.EXPORT_WRITE,
  PERMISSIONS.LOGISTICS_WRITE,
  PERMISSIONS.SALES_WRITE,
  PERMISSIONS.AFTERSALES_WRITE,
  PERMISSIONS.ENTERPRISE_WRITE
];

const OPS_READ_WRITE = [...READ_PERMS, ...WRITE_PERMS];

function expandPermissionKeys(keys) {
  const set = new Set();
  keys.forEach((k) => {
    set.add(k);
    (PERMISSION_ALIASES[k] || []).forEach((a) => set.add(a));
  });
  return [...set];
}

function perms(...keys) {
  const map = {};
  ALL_PERMS.forEach((k) => {
    map[k] = false;
  });
  expandPermissionKeys(keys).forEach((k) => {
    map[k] = true;
  });
  return map;
}

/**
 * Default role definitions stored in Firestore `roles` collection.
 */
export const ROLE_DEFINITIONS = {
  [ROLES.FULL_ADMIN]: {
    id: ROLES.FULL_ADMIN,
    name: "Full Admin / Super Admin",
    description: "Platform owner — full access to DMS, SCM foundation, users & roles.",
    level: ROLE_LEVEL[ROLES.FULL_ADMIN],
    category: "platform",
    permissions: perms(...ALL_PERMS)
  },
  [ROLES.ADMIN]: {
    id: ROLES.ADMIN,
    name: "Admin",
    description: "Bulk license buyer company — manage licenses, org users, and all modules.",
    level: ROLE_LEVEL[ROLES.ADMIN],
    category: "platform",
    permissions: perms(
      PERMISSIONS.MANAGE_USERS,
      PERMISSIONS.MANAGE_LICENSES,
      PERMISSIONS.MANAGE_ORG,
      PERMISSIONS.ACCESS_ADMIN_PANEL,
      PERMISSIONS.FOUNDATION_USERS,
      PERMISSIONS.MASTER_DATA_READ,
      PERMISSIONS.MASTER_DATA_WRITE,
      PERMISSIONS.AUDIT_READ,
      ...OPS_READ_WRITE
    )
  },
  [ROLES.COMPANY]: {
    id: ROLES.COMPANY,
    name: "Company",
    description: "Overseas license buyer — manage dealers and company operations.",
    level: ROLE_LEVEL[ROLES.COMPANY],
    category: "platform",
    permissions: perms(
      PERMISSIONS.MANAGE_USERS,
      PERMISSIONS.MANAGE_ORG,
      PERMISSIONS.ACCESS_ADMIN_PANEL,
      PERMISSIONS.FOUNDATION_USERS,
      PERMISSIONS.MASTER_DATA_READ,
      PERMISSIONS.AUDIT_READ,
      ...OPS_READ_WRITE
    )
  },
  [ROLES.DEALER]: {
    id: ROLES.DEALER,
    name: "Dealer",
    description: "Sub-dealer under a company — manage users and day-to-day modules.",
    level: ROLE_LEVEL[ROLES.DEALER],
    category: "platform",
    permissions: perms(
      PERMISSIONS.MANAGE_USERS,
      PERMISSIONS.ACCESS_ADMIN_PANEL,
      PERMISSIONS.FOUNDATION_USERS,
      PERMISSIONS.MASTER_DATA_READ,
      ...OPS_READ_WRITE
    )
  },
  [ROLES.USER]: {
    id: ROLES.USER,
    name: "User",
    description: "Standard account — read/write on operational DMS and inventory modules.",
    level: ROLE_LEVEL[ROLES.USER],
    category: "platform",
    permissions: perms(...OPS_READ_WRITE, PERMISSIONS.MASTER_DATA_READ)
  },
  [ROLES.VIEWER]: {
    id: ROLES.VIEWER,
    name: "Viewer",
    description: "View-only account — no write actions.",
    level: ROLE_LEVEL[ROLES.VIEWER],
    category: "platform",
    permissions: perms(...READ_PERMS)
  },
  [ROLES.AUDITOR]: {
    id: ROLES.AUDITOR,
    name: "Auditor / Local Accountant",
    description: "Read-only access to the company's invoices, accounts, statements and documents.",
    level: ROLE_LEVEL[ROLES.AUDITOR],
    category: "platform",
    permissions: perms(
      PERMISSIONS.DASHBOARD_ACCESS,
      PERMISSIONS.ACCOUNTING_READ,
      PERMISSIONS.FILES_READ
    )
  },
  [ROLES.MANAGEMENT]: {
    id: ROLES.MANAGEMENT,
    name: "Management",
    description: "Executive view across DMS and supply chain KPIs.",
    level: ROLE_LEVEL[ROLES.MANAGEMENT],
    category: "scm",
    permissions: perms(
      ...READ_PERMS,
      PERMISSIONS.ACCESS_ADMIN_PANEL,
      PERMISSIONS.AUDIT_READ,
      PERMISSIONS.MASTER_DATA_READ,
      PERMISSIONS.ENTERPRISE_WRITE,
      PERMISSIONS_V2.ENTERPRISE_REPORT_VIEW,
      PERMISSIONS_V2.ENTERPRISE_APPROVAL_MANAGE
    )
  },
  [ROLES.PROCUREMENT_MANAGER]: {
    id: ROLES.PROCUREMENT_MANAGER,
    name: "Procurement Manager",
    description: "Owns purchase requests/orders and supplier decisions.",
    level: ROLE_LEVEL[ROLES.PROCUREMENT_MANAGER],
    category: "scm",
    permissions: perms(
      ...READ_PERMS,
      PERMISSIONS.INVENTORY_WRITE,
      PERMISSIONS.ACCOUNTING_READ,
      PERMISSIONS.APPROVALS_WRITE,
      PERMISSIONS.MASTER_DATA_READ,
      PERMISSIONS.FILES_WRITE,
      PERMISSIONS_V2.PROCUREMENT_PO_CREATE,
      PERMISSIONS_V2.PROCUREMENT_PO_APPROVE
    )
  },
  [ROLES.PROCUREMENT_SPECIALIST]: {
    id: ROLES.PROCUREMENT_SPECIALIST,
    name: "Procurement Specialist",
    description: "Creates and tracks purchase requests and RFQs.",
    level: ROLE_LEVEL[ROLES.PROCUREMENT_SPECIALIST],
    category: "scm",
    permissions: perms(
      ...READ_PERMS,
      PERMISSIONS.INVENTORY_WRITE,
      PERMISSIONS.MASTER_DATA_READ,
      PERMISSIONS.FILES_WRITE,
      PERMISSIONS_V2.PROCUREMENT_PO_CREATE
    )
  },
  [ROLES.WAREHOUSE_MANAGER]: {
    id: ROLES.WAREHOUSE_MANAGER,
    name: "Warehouse Manager",
    description: "Manages warehouses, stock levels, and adjustments.",
    level: ROLE_LEVEL[ROLES.WAREHOUSE_MANAGER],
    category: "scm",
    permissions: perms(
      ...READ_PERMS,
      PERMISSIONS.INVENTORY_WRITE,
      PERMISSIONS.MASTER_DATA_READ,
      PERMISSIONS.FILES_WRITE,
      PERMISSIONS_V2.WAREHOUSE_INVENTORY_ADJUST
    )
  },
  [ROLES.WAREHOUSE_OPERATOR]: {
    id: ROLES.WAREHOUSE_OPERATOR,
    name: "Warehouse Operator",
    description: "Executes receipts, transfers, and picks.",
    level: ROLE_LEVEL[ROLES.WAREHOUSE_OPERATOR],
    category: "scm",
    permissions: perms(
      PERMISSIONS.DASHBOARD_ACCESS,
      PERMISSIONS.INVENTORY_READ,
      PERMISSIONS.INVENTORY_WRITE,
      PERMISSIONS.FILES_READ,
      PERMISSIONS.SEARCH_ACCESS,
      PERMISSIONS.MASTER_DATA_READ
    )
  },
  [ROLES.PRODUCTION_MANAGER]: {
    id: ROLES.PRODUCTION_MANAGER,
    name: "Production Manager",
    description: "Releases and monitors manufacturing orders.",
    level: ROLE_LEVEL[ROLES.PRODUCTION_MANAGER],
    category: "scm",
    permissions: perms(
      ...READ_PERMS,
      PERMISSIONS.INVENTORY_WRITE,
      PERMISSIONS.MANUFACTURING_WRITE,
      PERMISSIONS.MASTER_DATA_READ,
      PERMISSIONS_V2.MANUFACTURING_ORDER_RELEASE,
      PERMISSIONS_V2.MANUFACTURING_ORDER_MANAGE
    )
  },
  [ROLES.PRODUCTION_OPERATOR]: {
    id: ROLES.PRODUCTION_OPERATOR,
    name: "Production Operator",
    description: "Records production execution and material consumption.",
    level: ROLE_LEVEL[ROLES.PRODUCTION_OPERATOR],
    category: "scm",
    permissions: perms(
      PERMISSIONS.DASHBOARD_ACCESS,
      PERMISSIONS.INVENTORY_READ,
      PERMISSIONS.INVENTORY_WRITE,
      PERMISSIONS.MANUFACTURING_READ,
      PERMISSIONS.MANUFACTURING_WRITE,
      PERMISSIONS.FILES_READ,
      PERMISSIONS.MASTER_DATA_READ
    )
  },
  [ROLES.QUALITY_MANAGER]: {
    id: ROLES.QUALITY_MANAGER,
    name: "Quality Manager",
    description: "Owns quality plans and NCR approvals.",
    level: ROLE_LEVEL[ROLES.QUALITY_MANAGER],
    category: "scm",
    permissions: perms(
      ...READ_PERMS,
      PERMISSIONS.QUALITY_WRITE,
      PERMISSIONS.MASTER_DATA_READ,
      PERMISSIONS.FILES_WRITE,
      PERMISSIONS_V2.QUALITY_INSPECTION_APPROVE,
      PERMISSIONS_V2.QUALITY_NCR_MANAGE,
      PERMISSIONS_V2.QUALITY_PLAN_MANAGE
    )
  },
  [ROLES.QUALITY_INSPECTOR]: {
    id: ROLES.QUALITY_INSPECTOR,
    name: "Quality Inspector",
    description: "Performs inspections and records results.",
    level: ROLE_LEVEL[ROLES.QUALITY_INSPECTOR],
    category: "scm",
    permissions: perms(
      PERMISSIONS.DASHBOARD_ACCESS,
      PERMISSIONS.INVENTORY_READ,
      PERMISSIONS.QUALITY_READ,
      PERMISSIONS.QUALITY_WRITE,
      PERMISSIONS.FILES_READ,
      PERMISSIONS.FILES_WRITE,
      PERMISSIONS.MASTER_DATA_READ,
      PERMISSIONS.SEARCH_ACCESS,
      PERMISSIONS_V2.QUALITY_INSPECTION_RECORD
    )
  },
  [ROLES.LOGISTICS_MANAGER]: {
    id: ROLES.LOGISTICS_MANAGER,
    name: "Logistics Manager",
    description: "Owns shipments, carriers, and transit visibility.",
    level: ROLE_LEVEL[ROLES.LOGISTICS_MANAGER],
    category: "scm",
    permissions: perms(
      ...READ_PERMS,
      PERMISSIONS.INVENTORY_WRITE,
      PERMISSIONS.PACKAGING_WRITE,
      PERMISSIONS.LOGISTICS_WRITE,
      PERMISSIONS.MASTER_DATA_READ,
      PERMISSIONS.FILES_WRITE
    )
  },
  [ROLES.EXPORT_SPECIALIST]: {
    id: ROLES.EXPORT_SPECIALIST,
    name: "Export Specialist",
    description: "Prepares export documentation and customs packages.",
    level: ROLE_LEVEL[ROLES.EXPORT_SPECIALIST],
    category: "scm",
    permissions: perms(
      ...READ_PERMS,
      PERMISSIONS.EXPORT_WRITE,
      PERMISSIONS.LOGISTICS_READ,
      PERMISSIONS.MASTER_DATA_READ,
      PERMISSIONS.FILES_WRITE,
      PERMISSIONS.ACCOUNTING_READ,
      PERMISSIONS_V2.EXPORT_ORDER_MANAGE,
      PERMISSIONS_V2.EXPORT_DOC_MANAGE,
      PERMISSIONS_V2.CUSTOMS_DECLARE
    )
  },
  [ROLES.SALES_MANAGER]: {
    id: ROLES.SALES_MANAGER,
    name: "Sales Manager",
    description: "Owns customers, price lists, and sales orders.",
    level: ROLE_LEVEL[ROLES.SALES_MANAGER],
    category: "scm",
    permissions: perms(
      ...READ_PERMS,
      PERMISSIONS.SALES_WRITE,
      PERMISSIONS.AFTERSALES_WRITE,
      PERMISSIONS.INVENTORY_READ,
      PERMISSIONS.INVENTORY_WRITE,
      PERMISSIONS.ACCOUNTING_WRITE,
      PERMISSIONS.MASTER_DATA_READ,
      PERMISSIONS.FILES_WRITE,
      PERMISSIONS_V2.SALES_ORDER_MANAGE,
      PERMISSIONS_V2.SALES_FULFILLMENT,
      PERMISSIONS_V2.AFTERSALES_RETURN_MANAGE,
      PERMISSIONS_V2.AFTERSALES_RMA_MANAGE
    )
  },
  [ROLES.FINANCE]: {
    id: ROLES.FINANCE,
    name: "Finance",
    description: "Accounting, costs, and payment visibility.",
    level: ROLE_LEVEL[ROLES.FINANCE],
    category: "scm",
    permissions: perms(
      ...READ_PERMS,
      PERMISSIONS.ACCOUNTING_WRITE,
      PERMISSIONS.APPROVALS_WRITE,
      PERMISSIONS.MASTER_DATA_READ,
      PERMISSIONS.AUDIT_READ
    )
  }
};

export function normalizeRole(role) {
  if (!role) return ROLES.USER;
  const r = String(role).toLowerCase().trim();
  const aliases = {
    fulladmin: ROLES.FULL_ADMIN,
    "full-admin": ROLES.FULL_ADMIN,
    full_admin: ROLES.FULL_ADMIN,
    "super_admin": ROLES.FULL_ADMIN,
    "super-admin": ROLES.FULL_ADMIN,
    owner: ROLES.FULL_ADMIN,
    member: ROLES.USER,
    admin: ROLES.ADMIN,
    company: ROLES.COMPANY,
    dealer: ROLES.DEALER,
    user: ROLES.USER,
    viewer: ROLES.VIEWER,
    procurement_manager: ROLES.PROCUREMENT_MANAGER,
    procurement_specialist: ROLES.PROCUREMENT_SPECIALIST,
    warehouse_manager: ROLES.WAREHOUSE_MANAGER,
    warehouse_operator: ROLES.WAREHOUSE_OPERATOR,
    production_manager: ROLES.PRODUCTION_MANAGER,
    production_operator: ROLES.PRODUCTION_OPERATOR,
    quality_manager: ROLES.QUALITY_MANAGER,
    quality_inspector: ROLES.QUALITY_INSPECTOR,
    logistics_manager: ROLES.LOGISTICS_MANAGER,
    export_specialist: ROLES.EXPORT_SPECIALIST,
    sales_manager: ROLES.SALES_MANAGER,
    finance: ROLES.FINANCE,
    management: ROLES.MANAGEMENT,
    auditor: ROLES.AUDITOR,
    accountant: ROLES.AUDITOR,
    local_accountant: ROLES.AUDITOR
  };
  return aliases[r] || (ROLE_DEFINITIONS[r] ? r : ROLES.USER);
}

export function getRoleDefinition(role) {
  const id = normalizeRole(role);
  return ROLE_DEFINITIONS[id] || ROLE_DEFINITIONS[ROLES.USER];
}

export function getPermissionsForRole(role) {
  return { ...getRoleDefinition(role).permissions };
}

export function hasPermission(permissions, key) {
  if (!permissions || !key) return false;
  if (permissions[PERMISSIONS.MANAGE_ROLES] || permissions[PERMISSIONS_V2.FOUNDATION_ROLE_MANAGE]) {
    return true;
  }
  if (permissions[key]) return true;
  const aliases = PERMISSION_ALIASES[key] || [key];
  return aliases.some((a) => !!permissions[a]);
}

/** Admin Panel is a company-account surface, never the public demo login. */
export function isAdminPanelPermission(key) {
  if (!key) return false;
  return key === PERMISSIONS.ACCESS_ADMIN_PANEL || key === PERMISSIONS_V2.ADMIN_PANEL;
}

/**
 * Demo accounts may view and edit operational modules against in-memory sample data.
 * Admin Panel / license / role management stay with the licensed company email.
 */
export function getDemoBrowsePermissions() {
  const perms = getPermissionsForRole(ROLES.FULL_ADMIN);
  delete perms[PERMISSIONS.ACCESS_ADMIN_PANEL];
  delete perms[PERMISSIONS_V2.ADMIN_PANEL];
  delete perms[PERMISSIONS.MANAGE_ROLES];
  delete perms[PERMISSIONS_V2.FOUNDATION_ROLE_MANAGE];
  delete perms[PERMISSIONS.MANAGE_LICENSES];
  delete perms[PERMISSIONS_V2.LICENSE_MANAGE];
  return perms;
}

export function canWriteWithPermissions(permissions) {
  if (!permissions) return false;
  return WRITE_PERMS.some((k) => hasPermission(permissions, k));
}

export function getAssignableRoles(actorRole) {
  const actorLevel = ROLE_LEVEL[normalizeRole(actorRole)] || 0;
  return ROLE_ORDER.filter((r) => ROLE_LEVEL[r] < actorLevel);
}

/** Roles an org Admin can create (not another Admin / Full Admin) */
export const ORG_ADMIN_CREATABLE_ROLES = [
  ROLES.COMPANY,
  ROLES.DEALER,
  ROLES.USER,
  ROLES.VIEWER,
  ROLES.PROCUREMENT_MANAGER,
  ROLES.PROCUREMENT_SPECIALIST,
  ROLES.WAREHOUSE_MANAGER,
  ROLES.WAREHOUSE_OPERATOR,
  ROLES.PRODUCTION_MANAGER,
  ROLES.PRODUCTION_OPERATOR,
  ROLES.QUALITY_MANAGER,
  ROLES.QUALITY_INSPECTOR,
  ROLES.LOGISTICS_MANAGER,
  ROLES.EXPORT_SPECIALIST,
  ROLES.SALES_MANAGER,
  ROLES.FINANCE,
  ROLES.MANAGEMENT,
  ROLES.AUDITOR
];

/** Read-only roles: never granted a write permission, whatever is stored on the profile. */
export const READ_ONLY_ROLES = [ROLES.VIEWER, ROLES.AUDITOR];

export function isReadOnlyRole(role) {
  return READ_ONLY_ROLES.includes(normalizeRole(role));
}

export function getOrgSubtreeIds(allUsers, rootUid) {
  if (!rootUid) return new Set();
  const childrenByParent = {};
  (allUsers || []).forEach((u) => {
    const p = u.parentUid || null;
    if (!childrenByParent[p]) childrenByParent[p] = [];
    childrenByParent[p].push(u.id);
  });
  const ids = new Set([rootUid]);
  const queue = [rootUid];
  while (queue.length) {
    const id = queue.shift();
    (childrenByParent[id] || []).forEach((childId) => {
      if (!ids.has(childId)) {
        ids.add(childId);
        queue.push(childId);
      }
    });
  }
  return ids;
}

export function filterUsersInOrg(allUsers, rootUid) {
  const ids = getOrgSubtreeIds(allUsers, rootUid);
  return (allUsers || []).filter((u) => ids.has(u.id));
}

export function canManageUser(actorRole, targetRole) {
  const actorLevel = ROLE_LEVEL[normalizeRole(actorRole)] || 0;
  const targetLevel = ROLE_LEVEL[normalizeRole(targetRole)] || 0;
  return actorLevel > targetLevel;
}

export function isFullAdminEmail(email) {
  return String(email || "").trim().toLowerCase() === FULL_ADMIN_EMAIL;
}

/** Map route path prefixes to required permission */
export function permissionForPath(pathname) {
  if (!pathname) return null;
  if (pathname.includes("/foundation/users")) return PERMISSIONS.FOUNDATION_USERS;
  if (pathname.includes("/foundation/master-data")) return PERMISSIONS.MASTER_DATA_READ;
  if (pathname.includes("/foundation/audit")) return PERMISSIONS.AUDIT_READ;
  if (pathname.includes("/procurement")) return PERMISSIONS.PROCUREMENT_READ;
  if (pathname.includes("/manufacturing")) return PERMISSIONS.MANUFACTURING_READ;
  if (pathname.includes("/quality")) return PERMISSIONS.QUALITY_READ;
  if (pathname.includes("/packaging")) return PERMISSIONS.PACKAGING_READ;
  if (pathname.includes("/export")) return PERMISSIONS.EXPORT_READ;
  if (pathname.includes("/sales")) return PERMISSIONS.SALES_READ;
  if (pathname.includes("/after-sales")) return PERMISSIONS.AFTERSALES_READ;
  if (pathname.includes("/enterprise")) return PERMISSIONS.ENTERPRISE_READ;
  if (pathname.includes("/admin-panel")) return PERMISSIONS.ACCESS_ADMIN_PANEL;
  if (pathname.includes("/inventory")) return PERMISSIONS.INVENTORY_READ;
  if (pathname.includes("/accounting")) return PERMISSIONS.ACCOUNTING_READ;
  if (pathname.includes("/files") || pathname.includes("/folders")) return PERMISSIONS.FILES_READ;
  if (pathname.includes("/ai-chat") || pathname.includes("/ai/analysis") || pathname.includes("/ocr")) return PERMISSIONS.AI_ACCESS;
  if (pathname.includes("/search")) return PERMISSIONS.SEARCH_ACCESS;
  if (pathname.includes("/dashboard") || pathname.includes("/index")) return PERMISSIONS.DASHBOARD_ACCESS;
  return null;
}

export function sidebarPermissionForRoute(route) {
  if (route?.permission) return route.permission;
  const path = `${route?.layout || ""}${route?.path || ""}`;
  return permissionForPath(path);
}
